import { useEffect, useState, useSyncExternalStore } from "react";
import { MemoizedValue, Memoizer } from "../helpers/memoizer";
import useRealtimeParamsData from "./use-realtime-params-data";
import { ParamsWatcher, getLastReadings } from "../helpers/params";
import { Lang } from "../helpers/i18n";

export default function useMemoizedParamsData<K extends string>(
    memoizer: Memoizer,
    watcher: ParamsWatcher,
    lang: Lang,
    params: [storeKey: K, path: string, update: number, priority?: "low" | "normal" | "high"][],
): [Readonly<Partial<Record<K, MemoizedValue>>>, boolean] {
    const [isReady, setIsReady] = useState(false);
    const watchedKeys = params.map(([key]) => key);

    useRealtimeParamsData(watcher, [...params]);

    useEffect(() => {
        memoizer.addKeysToWatch(watchedKeys);
        memoizer.addOnReady(() => setIsReady(true));

        (async () => {
            if (watchedKeys.length === 0) {
                return;
            }
            const resp = await getLastReadings(watchedKeys, lang, watcher.id.bind(watcher));
            memoizer.setDefaultValues(resp.data);
            memoizer.setLastUpdate(resp.lastReadOn);
        })().catch((e) => {
            // eslint-disable-next-line no-console
            console.error(`Can't get last readings: ${(e as Error).message}`);
        });
    }, [watchedKeys.join(",")]);

    return [
        useSyncExternalStore(memoizer.memoizedData.subscribe, memoizer.memoizedData.getSnapshot),
        isReady,
    ];
}
